import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { NgxDropzoneChangeEvent } from 'ngx-dropzone';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {SnackBarService} from '../../../services/utils/snackBar.service';
import {CompaniesService} from '../../../services/modules/companies.service';
import {environment} from '../../../../../environments/environment';
import {DialogService} from '../../../services/utils/dialog.service';

type FileType = 'inicial' | 'correct' | 'incorrect';

export interface DialogData {
  title: string;
  data?: any;
  question: string;
  idTraining: number;
}

@Component({
  selector: 'app-dialog-question-settings',
  templateUrl: './dialog-question-settings.component.html',
  styleUrls: ['./dialog-question-settings.component.scss'],
})
export class DialogQuestionSettingsComponent implements OnInit {
  dataEdit: any;
  s3Bucket: string;
  trainingId: number;
  questionForm: FormGroup;
  fileWasDeleted = false;
  hasExistingFile = false;
  hasExistingContextFile = false;
  correctAnswerImageDeleted = false;
  incorrectAnswerImageDeleted = false;

  files: File[] = [];
  fileCorrectQuestion: File[] = [];
  fileIncorrectQuestion: File[] = [];

  private tempPreviewCorrect: string | null = null;
  private tempPreviewIncorrect: string | null = null;

  constructor(
    private fb: FormBuilder,
    private dialogService: DialogService,
    private snackBService: SnackBarService,
    private companiesServices: CompaniesService,
    public dialogRef: MatDialogRef<DialogQuestionSettingsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {
    this.questionForm = this.fb.group({
      initialUrl: [''],
      questionOrder: [null],
      Question: ['', Validators.compose([Validators.required, Validators.maxLength(250)])],
      respuestaCorrecta: ['', Validators.compose([Validators.required, Validators.maxLength(250)])],
      respuestaIncorrecta: ['', Validators.compose([Validators.required, Validators.maxLength(250)])],
    });
    this.dataEdit = data.data;
  }

  ngOnInit(): void {
    this.s3Bucket = environment.api.s3Bucket;
    this.trainingId = this.data.idTraining;

    if (this.data.question) {
      this.questionForm.get('pregunta')?.setValue(this.data.question);
    }

    const initialUrlControl: AbstractControl | null = this.questionForm.get('initialUrl');
    if (initialUrlControl) {
      initialUrlControl.valueChanges.subscribe(val => {
        const urlValue = (val || '').toString().trim();
        if (urlValue !== '') {
          this.files = [];
        }
      });
    }
    if (this.dataEdit !== undefined) {
      this.initializeEditData();
    }
  }

  private initializeEditData(): void {
    if (this.dataEdit.ContextMediaType === 3) {
      this.questionForm.get('initialUrl')?.setValue(this.dataEdit.initialUrl);
    }
    this.questionForm.get('questionOrder')?.setValue(this.dataEdit.Order);
    this.questionForm.get('Question')?.setValue(this.dataEdit.Question);
    this.questionForm.get('respuestaCorrecta')?.setValue(this.dataEdit.CorrectAnswerText);
    this.questionForm.get('respuestaIncorrecta')?.setValue(this.dataEdit.IncorrectAnswerText);
    this.hasExistingFile = !!this.dataEdit?.ContextQuestion;
    this.correctAnswerImageDeleted = false;
    this.incorrectAnswerImageDeleted = false;
    this.fileWasDeleted = false;
    this.hasExistingContextFile = !!this.dataEdit?.ContextQuestion;
  }

  get hasUrl(): boolean {
    const control = this.questionForm.get('initialUrl');
    if (!control) {
      return false;
    }
    const v = control.value;
    return v !== null && v !== undefined && v.toString().trim() !== '';
  }

  get contextDisplayName(): string {
    if (this.files.length > 0) {
      return this.files[0].name;
    }
    if (!this.dataEdit || this.fileWasDeleted) {
      return '';
    }
    switch (this.dataEdit.ContextMediaType) {
      case 0:
        return 'Video';
      case 1:
        return 'Archivo';
      case 2:
        return 'Audio';
      case 3:
        return this.dataEdit.ContextQuestion;
      case 4:
        return 'Imagen';
      case 5:
      default:
        return '';
    }
  }

  onSelect(event: NgxDropzoneChangeEvent, type: FileType): void {
    const selected = [...event.addedFiles];

    if ((type === 'correct' || type === 'incorrect') && selected.length > 0) {
      const file = selected[0];
      if (!file.type.startsWith('image/')) {
        this.snackBService.openWarning('Solo se permiten archivos de imagen para esta opción.', '', 4000);
        return;
      }
    }

    switch (type) {
      case 'inicial':
        this.questionForm.get('initialUrl')?.setValue('');
        this.files = selected;
        this.hasExistingContextFile = false;
        this.hasExistingFile = true;
        this.fileWasDeleted = false;
        break;

      case 'correct':
        this.revokeTempPreview('correct');
        this.fileCorrectQuestion = selected;
        this.tempPreviewCorrect = selected.length ? URL.createObjectURL(selected[0]) : null;
        this.correctAnswerImageDeleted = false;
        break;

      case 'incorrect':
        this.revokeTempPreview('incorrect');
        this.fileIncorrectQuestion = selected;
        this.tempPreviewIncorrect = selected.length ? URL.createObjectURL(selected[0]) : null;
        this.incorrectAnswerImageDeleted = false;
        break;
    }
  }

  private revokeTempPreview(which: 'correct' | 'incorrect'): void {
    if (which === 'correct' && this.tempPreviewCorrect) {
      URL.revokeObjectURL(this.tempPreviewCorrect);
      this.tempPreviewCorrect = null;
    }
    if (which === 'incorrect' && this.tempPreviewIncorrect) {
      URL.revokeObjectURL(this.tempPreviewIncorrect);
      this.tempPreviewIncorrect = null;
    }
  }

  clearInitialSelection(): void {
    this.files = [];
    this.hasExistingFile = false;
    this.hasExistingContextFile = false;
    this.fileWasDeleted = false;
    this.questionForm.get('initialUrl')?.setValue('');
  }

  removeFileCorrect(): void {
    this.revokeTempPreview('correct');
    this.fileCorrectQuestion = [];
  }

  removeFileIncorrect(): void {
    this.revokeTempPreview('incorrect');
    this.fileIncorrectQuestion = [];
  }

  close(): void {
    this.dialogRef.close(false);
  }

  save(): void {
    if (!this.validateBeforeSave()) { return; }
    const formData = this.buildCreateFormData();
    this.companiesServices.postTrainingQuestionComplete(this.trainingId, formData).subscribe(
      () => {
        this.snackBService.openSuccess('¡PREGUNTA Y RESPUESTAS CREADAS CORRECTAMENTE!', '', 5000);
        this.dialogRef.close(true);
        window.location.reload();
      },
      (error) => {
        if (error.status === 420) {
          const msg = error.error?.message || 'La capacitación supera el tamaño permitido.';
          this.snackBService.openWarning(msg, '', 5000);
        } else {
          this.snackBService.openWarning('Error guardando datos.', '', 5000);
        }
      }
    );
  }

  saveEdit(): void {
    if (!this.validateBeforeSave()) { return; }
    const questionId = this.dataEdit.IdQuestion;
    const formData = this.buildUpdateFormData();
    this.companiesServices.putTrainingQuestionComplete(questionId, formData).subscribe(
      () => {
        this.snackBService.openSuccess('¡PREGUNTA Y RESPUESTAS EDITADAS CORRECTAMENTE!', '', 5000);
        this.dialogRef.close(true);
        window.location.reload();
      },
      (error) => {
        if (error.status === 420) {
          const msg = error.error?.message || 'La capacitación supera el tamaño permitido.';
          this.snackBService.openWarning(msg, '', 5000);
        } else {
          this.snackBService.openWarning('Error actualizando datos.', '', 5000);
        }
      }
    );
  }

  private buildCreateFormData(): FormData {
    const formData = new FormData();
    formData.append('Text', this.questionForm.get('Question')?.value || '');
    formData.append('Order', (this.questionForm.get('questionOrder')?.value || 0).toString());

    if (this.files.length > 0) {
      formData.append('ContextMedia', this.files[0]);
    } else if (this.hasUrl) {
      formData.append('ContextUrl', this.questionForm.get('initialUrl')?.value || '');
    }

    formData.append('CorrectAnswer.Text', this.questionForm.get('respuestaCorrecta')?.value || '');
    if (this.fileCorrectQuestion.length > 0) {
      formData.append('CorrectAnswer.Image', this.fileCorrectQuestion[0]);
    }

    formData.append('IncorrectAnswer.Text', this.questionForm.get('respuestaIncorrecta')?.value || '');
    if (this.fileIncorrectQuestion.length > 0) {
      formData.append('IncorrectAnswer.Image', this.fileIncorrectQuestion[0]);
    }
    return formData;
  }

  private buildUpdateFormData(): FormData {
    const formData = new FormData();

    formData.append('Text', this.questionForm.get('Question')?.value || '');
    formData.append('Order', (this.questionForm.get('questionOrder')?.value || 0).toString());

    formData.append('QuestionId', this.dataEdit.IdQuestion.toString());
    if (this.dataEdit.CorrectAnswerId) {
      formData.append('CorrectAnswerId', this.dataEdit.CorrectAnswerId.toString());
    }
    if (this.dataEdit.IncorrectAnswerId) {
      formData.append('IncorrectAnswerId', this.dataEdit.IncorrectAnswerId.toString());
    }

    this.handleContextMediaInFormData(formData);
    this.handleAnswersInFormData(formData);

    formData.forEach((value, key) => {
      if (value instanceof File) {
      } else {
      }
    });
    return formData;
  }

  private handleContextMediaInFormData(formData: FormData): void {
    if (this.files.length > 0) {
      formData.append('ContextMedia', this.files[0]);
      if (this.fileWasDeleted) {
        formData.append('RemoveContextMedia', 'true');
      }
      return;
    }

    if (this.hasUrl) {
      formData.append('ContextUrl', this.questionForm.get('initialUrl')?.value || '');
      if (this.fileWasDeleted) {
        formData.append('RemoveContextMedia', 'true');
      }
      return;
    }

    if (this.fileWasDeleted) {
      formData.append('RemoveContextMedia', 'true');
    }
  }

  private handleAnswersInFormData(formData: FormData): void {
    formData.append('CorrectAnswer.Text', this.questionForm.get('respuestaCorrecta')?.value || '');

    if (this.correctAnswerImageDeleted) {
      formData.append('RemoveCorrectAnswerImage', 'true');
    } else if (this.fileCorrectQuestion.length > 0) {
      formData.append('CorrectAnswer.Image', this.fileCorrectQuestion[0]);
    }

    formData.append('IncorrectAnswer.Text', this.questionForm.get('respuestaIncorrecta')?.value || '');

    if (this.incorrectAnswerImageDeleted) {
      formData.append('RemoveIncorrectAnswerImage', 'true');
    } else if (this.fileIncorrectQuestion.length > 0) {
      formData.append('IncorrectAnswer.Image', this.fileIncorrectQuestion[0]);
    }
  }

  private validateBeforeSave(): boolean {
    if (!this.questionForm.valid) {
      this.snackBService.openWarning('Por favor completa todos los campos requeridos.', '', 5000);
      return false;
    }
    return true;
  }

  openUrl(url: string): void {
    const isExternal = /^https?:\/\//.test(url);
    const finalUrl = isExternal ? url : this.s3Bucket + url;
    window.open(finalUrl, '_blank');
  }

  deleteContextMedia(questionId): void {
    this.dialogService
      .openDialogConfirm('¿Estás seguro de eliminar el contexto inicial?', true, true, 'Cancelar')
      .afterClosed()
      .subscribe((result) => {
        if (!result) { return; }

        this.companiesServices.deleteInitialContext(questionId).subscribe(
          () => {
            this.hasExistingFile = false;
            this.hasExistingContextFile = false;
            this.files = [];
            this.questionForm.get('initialUrl')?.setValue('');

            if (this.dataEdit) {
              this.dataEdit.ContextQuestion = null;
              this.dataEdit.ContextMediaType = 5;
            }

            this.fileWasDeleted = false;
            this.snackBService.openSuccess('Contexto inicial eliminado correctamente.', '', 3000);
          },
          () => {
            this.snackBService.openWarning('No se pudo eliminar el contexto inicial.', '', 5000);
          }
        );
      });
  }


  deleteQuestionAnswer(isCorrect: boolean): void {
    const questionOrAnswer = isCorrect ? this.dataEdit.CorrectAnswerId : this.dataEdit.IncorrectAnswerId;
    this.dialogService.openDialogConfirm('¿Estás seguro de eliminar la imagen de la respuesta?', true, true, 'Cancelar')
      .afterClosed().subscribe((result) => {
      if (result) {
        this.companiesServices.deleteQuestion(questionOrAnswer).subscribe(
          () => {
            if (isCorrect) {
              this.dataEdit.CorrectAnswerImage = null;
              this.fileCorrectQuestion = [];
              this.correctAnswerImageDeleted = true;
            } else {
              this.dataEdit.ImageIncorrectAnswer = null;
              this.fileIncorrectQuestion = [];
              this.incorrectAnswerImageDeleted = true;
            }
            this.snackBService.openWarning('Imagen marcada para eliminar', '', 5000);
          });
      }
    });
  }

}
